/**
 * Icon mapping utility for the Fluent UI → shadcn/ui migration.
 *
 * PbsIcon is a drop-in replacement for Fluent UI's v8 <Icon iconName="..." />
 * and v9 named icon components (e.g. Dismiss24Regular).
 *
 * Usage:
 *   // v8 style
 *   <PbsIcon iconName="Calendar" />
 *
 *   // v9 named icon replacement
 *   import { Dismiss24Regular as DismissIcon } from '@/components/common/icons';
 *   <DismissIcon className="h-4 w-4" />
 */

import React from 'react';
import {
  ArrowUpFromLine,
  Bell,
  Calendar,
  ClipboardList,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  DollarSign,
  Edit,
  FilePen,
  FileText,
  FolderOpen,
  GraduationCap,
  HeartPulse,
  Home,
  Info,
  Inbox,
  LayoutGrid,
  Mail,
  Settings,
  ShieldCheck,
  Star,
  Table,
  Trophy,
  Upload,
  User,
  UserCheck,
  UserPlus,
  Users,
  Video,
  X,
  type LucideIcon,
} from 'lucide-react';

// ─────────────────────────────────────────────
// Type definitions
// ─────────────────────────────────────────────

export interface IconProps {
  className?: string;
  size?: number;
}

type LucideIconComponent = LucideIcon;

// ─────────────────────────────────────────────
// v8 iconName string → lucide-react map
// ─────────────────────────────────────────────

const ICON_MAP: Record<string, LucideIconComponent> = {
  // Navigation
  Calendar,
  ChevronLeft,
  ChevronRight,
  Home,
  Settings,

  // Actions
  Cancel: X,
  Upload: ArrowUpFromLine,

  // Content / misc
  Bell,
  ClipboardList,
  CertifiedDatabase: ShieldCheck,
  Clock,
  DocumentSet: FolderOpen,
  DollarSign,
  EditNote: FilePen,
  Education: GraduationCap,
  FileText,
  FormLibrary: FolderOpen,
  HeartPulse,
  Info,
  InboxCheck: Inbox,
  Mail,
  People: Users,
  Users,
  UserCheck,
  UserPlus,
  SkypeCircleCheck: CheckCircle,
  Touch: User,
  Video,
};

/**
 * Drop-in replacement for Fluent UI v8's <Icon iconName="..." />
 */
export const PbsIcon: React.FC<IconProps & { iconName: string }> = ({
  iconName,
  className,
  size = 16,
}) => {
  const Comp = ICON_MAP[iconName];
  if (!Comp) {
    // Render nothing for unknown icons in production; log in dev
    if (process.env.NODE_ENV === 'development') {
      //console.warn(`PbsIcon: no mapping for iconName="${iconName}"`);
    }
    return null;
  }
  return <Comp className={className} size={size} />;
};

// ─────────────────────────────────────────────
// Named exports to replace Fluent UI v9 icon imports
// ─────────────────────────────────────────────
// Usage: import { Dismiss24Regular } from '@/components/common/icons';

export const ArrowUpload24Regular: React.FC<IconProps> = (p) => <Upload {...p} />;
export const Calendar16Regular: React.FC<IconProps> = (p) => <Calendar {...p} />;
export const ChevronDown16Regular: React.FC<IconProps> = (p) => <ChevronDown {...p} />;
export const ChevronDown24Regular: React.FC<IconProps> = (p) => <ChevronDown {...p} />;
export const ChevronRight12Regular: React.FC<IconProps> = (p) => <ChevronRight {...p} />;
export const ChevronUp16Regular: React.FC<IconProps> = (p) => <ChevronUp {...p} />;
export const ChevronUp24Regular: React.FC<IconProps> = (p) => <ChevronUp {...p} />;
export const Clock16Regular: React.FC<IconProps> = (p) => <Clock {...p} />;
export const Dismiss12Regular: React.FC<IconProps> = (p) => <X {...p} />;
export const Dismiss24Regular: React.FC<IconProps> = (p) => <X {...p} />;
export const Document24Regular: React.FC<IconProps> = (p) => <FileText {...p} />;
export const Edit16Regular: React.FC<IconProps> = (p) => <Edit {...p} />;
export const Note16Regular: React.FC<IconProps> = (p) => <FileText {...p} />;
export const Open16Regular: React.FC<IconProps> = (p) => <FolderOpen {...p} />;
export const Person12Regular: React.FC<IconProps> = (p) => <User {...p} />;
export const Star16Filled: React.FC<IconProps> = (p) => <Star {...p} fill="currentColor" />;
export const Star16Regular: React.FC<IconProps> = (p) => <Star {...p} />;
export const Table16Regular: React.FC<IconProps> = (p) => <Table {...p} />;
export const Trophy16Regular: React.FC<IconProps> = (p) => <Trophy {...p} />;

// Waffle / app launcher grid (used in header)
export const WaffleIcon: React.FC<IconProps> = (p) => <LayoutGrid {...p} />;

// Convenience re-exports used directly in components
export {
  Bell,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Edit,
  FilePen,
  FileText,
  FolderOpen,
  GraduationCap,
  Home,
  Info,
  Inbox,
  LayoutGrid,
  Mail,
  Settings,
  ShieldCheck,
  Star,
  Trophy,
  User,
  Users,
  Video,
  X,
};
